import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";

import { yupResolver } from "@hookform/resolvers/yup";
import { getCountryCodeFromCurrency } from "@shared/components/ui-kit/form/PhoneNumberInput/utils";
import { PhoneCountryType } from "@shared/lib/libphonenumber/api";
import "@shared/lib/yupExtensions/index";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";
import FormErrorMessage from "@ui-kit/form/FormErrorMessage/FormErrorMessage.web";
import Input from "@ui-kit/form/Input/Input.web";
import PhoneNumberInput from "@ui-kit/form/PhoneNumberInput/PhoneNumberInput.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { guestCheckoutSchema } from "../../../utils/schema";
import type {
  GuestCheckoutFormData,
  GuestCheckoutFormSharedProps,
} from "./GuestCheckoutForm.props";

export type GuestCheckoutFormProps = GuestCheckoutFormSharedProps & {
  // Add any web specific props here.
};

export default function GuestCheckoutForm(
  props: GuestCheckoutFormProps
): JSX.Element {
  const { onContinueClick, isLoading } = props;
  const { t } = useTranslation("common");
  const { currency } = usePublicBasket();

  const [phoneCountry, setPhoneCountry] = useState<PhoneCountryType>({
    countryCode: getCountryCodeFromCurrency(currency),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<GuestCheckoutFormData>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - types are correct for yupResolver
    resolver: yupResolver(guestCheckoutSchema(phoneCountry)),
  });

  return (
    <form onSubmit={handleSubmit(onContinueClick)} data-sentry-mask>
      <Flex gap="lg" direction="column">
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <FormControl isInvalid={Boolean(errors.name)}>
              <FormLabel htmlFor="name">{t("Full name")}</FormLabel>
              <Input
                {...field}
                id="name"
                placeholder={t("Full name")}
                data-testid="full-name-input"
              />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            </FormControl>
          )}
        />
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <FormControl isInvalid={Boolean(errors.email)}>
              <FormLabel htmlFor="email">{t("Email address")}</FormLabel>
              <Input
                {...field}
                id="email"
                placeholder={t("Email address")}
                data-testid="email-address-input"
              />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>
          )}
        />
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <FormControl isInvalid={Boolean(errors.phone)}>
              <FormLabel htmlFor="phone">{t("Phone number")}</FormLabel>
              <PhoneNumberInput
                {...field}
                placeholder={t("Phone number")}
                handleSetPhone={(value, country) => {
                  field.onChange(value);
                  setPhoneCountry(country);
                }}
                phoneInput={field.value}
                countryInput={phoneCountry}
              />
              <FormErrorMessage>{errors.phone?.message}</FormErrorMessage>
            </FormControl>
          )}
        />

        <Button
          width="100%"
          variant="solid"
          isLoading={isSubmitting || isLoading}
          type="submit"
          data-testid="submit-guest-form-button"
        >
          {t("Continue")}
        </Button>
      </Flex>
    </form>
  );
}
